import { useCallback, useEffect, useState } from 'react';
import { getMetaAdPreview } from '../../services/metaAdsService';
import { getMetaErrorMessage } from '../metaAdsConfig';
import { normalizeMetaAdPreview } from '../metaAdPreview';

/**
 * Vista previa de un anuncio (ajuste 12): pide `getmetaadpreview` al montar, al cambiar
 * el formato y con `retry`; aborta el pedido anterior. La URL vive solo en este estado
 * local (vence: no va a estado global ni a localStorage). `formats` conserva la última
 * lista que mandó la API (para el selector mientras recarga).
 *
 * @param {{ adId: string, ownerUserId?: string|null }} params
 */
export function useMetaAdPreview({ adId, ownerUserId = null }) {
  const [format, setFormat] = useState(null); // null = default de la API
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState({ loading: true, data: null, error: null, formats: [] });

  useEffect(() => {
    if (!adId) return undefined;
    const controller = new AbortController();
    setState((prev) => ({ ...prev, loading: true, data: null, error: null }));
    getMetaAdPreview({ adId, format, ownerUserId, signal: controller.signal })
      .then((res) => {
        if (controller.signal.aborted) return;
        if (!res?._httpOk) {
          // `error` de la API ya es amigable; los `code` conocidos tienen su texto.
          const apiText = typeof res?.error === 'string' && res.error.trim() ? res.error.trim() : undefined;
          setState((prev) => ({ ...prev, loading: false, data: null, error: getMetaErrorMessage(res, apiText) }));
          return;
        }
        const data = normalizeMetaAdPreview(res.data);
        setState((prev) => ({ loading: false, data, error: null, formats: data.formats.length ? data.formats : prev.formats }));
      })
      .catch((err) => {
        if (err?.name === 'AbortError' || controller.signal.aborted) return;
        setState((prev) => ({ ...prev, loading: false, data: null, error: getMetaErrorMessage(null) }));
      });
    return () => controller.abort();
  }, [adId, format, ownerUserId, attempt]);

  const retry = useCallback(() => setAttempt((n) => n + 1), []);
  return {
    ...state,
    // Formato elegido; antes de elegir, el que respondió la API.
    format: format || state.data?.format || null,
    setFormat,
    retry,
    attempt,
  };
}

export default useMetaAdPreview;
